import { atom } from "jotai";
import { RoundOptions } from "../bulk-upload";

export const pricingTemplatesAtom = atom<IPricingTemplate[]>([]);
export const populatedPricingTemplatesAtom = atom<IPopulatedPricingTemplate[]>([]);
export const pricingTemplatesLoadingAtom = atom<boolean>(false);
export const internalPricingAtom = atom<IBrandPricing[] | null>(null);
export const pricingTemplateSourcesAtom = atom<IPricingSource[] | null>(null);
export const allPricingTemplateSourcesAtom = atom<IPricingSource[] | null>(null);
export const costBreakdownAtom = atom<ICostBreakdownResponse | null>(null);
export const referenceRatesAtom = atom<IReferenceRatesResponse | null>(null);

export interface IPricingTemplate {
    id?: number;
    created_at: string;
    brand_id: number;
    brand_pricing_id: number;
    name: string;
}

export interface IPopulatedPricingTemplate {
    id?: number;
    created_at: string;
    brand_id: number;
    name: string;
    pricings: IBrandPricing[];
    update_all_products: boolean;
    round_by: RoundOptions;
}

// Remediation bucket for a failed product — mirrors the backend enum.
export type PricingUpdateFailureCategory =
    | 'unpriceable_sku'
    | 'shopify_rejected'
    | 'not_published'
    | 'product_missing'
    | 'no_variants'
    | 'no_channel'
    | 'transient'
    | 'unknown';

export interface IPricingUpdateFailure {
    design_id: number;
    design_title?: string | null;
    shopify_product_id: string | null;
    message: string;
    category?: PricingUpdateFailureCategory;
}

// A background "update all products" job — mirrors order.pricing_update_jobs.
export interface IPricingUpdateJob {
    id: number;
    brand_id: number;
    pricing_template_id: number;
    status: 'unprocessed' | 'processing' | 'processed' | 'failed';
    total: number;
    completed: number;
    failed: number;
    // Capped server-side; `failed` is the true count.
    failures?: IPricingUpdateFailure[];
    error_message?: string | null;
    created_at?: string;
    updated_at?: string;
    round_by?: string;
}

export interface IBrandPricing {
    id?: number;
    created_at?: string;
    brand_id: number;
    price: number;
    pricing_template_id: number;
    pricing_source: IPricingSource;
}

export interface IPricingSource {
    id: number;
    created_at: string;
    source: string;
    default_price: number;
    charge_trigger: string;
    is_pricing_template_field: boolean;
    source_display_name: string;
}

// A single resolved fee line for a brand (per-brand override or the source default).
export interface ICostBreakdownSource {
    source: string;
    source_display_name: string;
    charge_trigger: string;
    price: number | null;
    default_price: number;
    credit_amount: number;
    is_overridden: boolean;
    /**
     * DISPLAY-ONLY provenance, INFERRED server-side by comparing the price to the
     * account-type tier default and the global source default. A brand override
     * that happens to equal a default reads as that default — there is
     * deliberately no is_overridden column on the tier table.
     */
    resolved_from?: PricingTier;
    account_type_default_price?: number | null;
}

export type PricingTier = 'BRAND_OVERRIDE' | 'ACCOUNT_TYPE_DEFAULT' | 'GLOBAL_DEFAULT';

// Hardcoded reference fees that live in the order-handler billing service (not the DB).
export interface ICostBreakdownStaticFees {
    consumable_paste_per_width: number;
    consumable_squeegee_per_width: number;
    consumable_line_surcharge: number;
    flat_rate_sample_shipping: number;
    flat_rate_box_shipping: number;
    sqft_width_factor: number;
    top_up_floor: number;
    mockup_tokens_per_mockup: number;
    mockup_tokens_per_premium_mockup: number;
}

export interface ICostBreakdownResponse {
    brand_id: number;
    sources: ICostBreakdownSource[];
    static_fees: ICostBreakdownStaticFees;
    account_type?: { id: number; name: string; display_name?: string } | null;
    /**
     * There is currently NO promotion audit table in the backend — the only trail
     * is a CloudWatch log line — so these arrive null. The UI must say
     * "not recorded" rather than implying a promotion never happened.
     */
    promoted_at?: string | null;
    promotion_trigger?: string | null;
}

// #region Account type pricing defaults (SUPERADMIN)
// The GLOBAL per-account-type pricing tier. Consulted ONLY when seeding a new
// brand's cost rows and when promoting a brand to STORE_OWNER — editing a value
// never re-prices an existing brand.
export interface IAccountTypeWithBrandCount {
    id: number;
    name: string;
    display_name?: string;
    /** How many brands sit on this type today, i.e. how many are already stamped. */
    brand_count: number;
}

export interface IAccountTypePricingDefault {
    account_type_id: number;
    pricing_source_id: number;
    default_price: number;
}

export interface IAccountTypeDefaultsResponse {
    account_types: IAccountTypeWithBrandCount[];
    pricing_sources: IPricingSource[];
    defaults: IAccountTypePricingDefault[];
}

// #region Account-type pricing sync (SUPERADMIN)
// Applying edited account-type defaults to brands that ALREADY exist. This is a
// FORCE OVERWRITE of each brand's cost rows — order.pricings has no provenance
// column, so a negotiated rate is indistinguishable from a stamped one and the
// exclusion list is the only safeguard.

/** A brand permanently exempt from the sync. */
export interface IPricingSyncExclusion {
    brand_id: number;
    brand_name: string;
    created_at?: string | null;
    created_by_user_id?: number | null;
    note?: string | null;
    /**
     * True when the entry comes from the brand's own `pricing_protected` flag rather
     * than the manual exclusion list. Protected entries are read-only here — the
     * toggle lives on the brand detail page.
     */
    is_protected?: boolean;
}

/**
 * The change for ONE pricing source, aggregated across every affected brand.
 *
 * Aggregated on purpose: production holds hundreds of thousands of cost rows, so a
 * per-brand diff is neither transferable nor renderable. `old_price_min/max` and
 * `distinct_old_prices` tell you whether the affected brands all sat on one value
 * or a spread of negotiated ones. Per-row detail lives in order.pricing_sync_backup.
 */
export interface IPricingSyncSourceSummary {
    run_id: string;
    pricing_source_id: number;
    source: string;
    source_display_name: string;
    new_price: number;
    brands_affected: number;
    rows_changed: number;
    old_price_min: number;
    old_price_max: number;
    distinct_old_prices: number;
}

export interface IPricingSyncResult {
    /** Null when nothing changed, or on a preview with no rows. */
    run_id: string | null;
    dry_run: boolean;
    account_type_id: number;
    brands_affected: number;
    rows_changed: number;
    excluded_brand_ids: number[];
    /** Brands skipped because they are pricing-protected at the brand level. */
    protected_brands_skipped: number;
    protected_brand_ids: number[];
    sources: IPricingSyncSourceSummary[];
}

// #region Licensing fee config
// The Wallmates cut of a brand's LICENSING income (not its order costs).
export type LicensingFeeType = 'PERCENTAGE' | 'FLAT';

export interface ILicensingFeeConfig {
    brand_id: number;
    fee_type: LicensingFeeType;
    fee_value: number;
    /** true = no brand row exists and the global fallback applies. */
    is_default: boolean;
}

/**
 * The GLOBAL default licensing fee every brand without an override resolves to.
 * Editable by SUPERADMIN/SUPERUSER from the Account Type Pricing admin page.
 * `updated_at` is null while no row has been saved yet and the backend's
 * hardcoded fallback is still in force.
 */
export interface ILicensingFeeDefault {
    fee_type: LicensingFeeType;
    fee_value: number;
    updated_at: string | null;
}

// #region Fulfillment Fee reference rates
// The GLOBAL Wallmates reference rate set used only to size the Fulfillment Fee
// (2.75% of a reference order total). These are NOT brand wholesale rates and are
// never shown to brands — SUPERADMIN only.
export interface IReferenceRate {
    source: string;
    source_display_name: string;
    price: number;
    pricing_id?: number;
}

export interface IReferenceRatesResponse {
    template_id: number;
    template_name: string;
    // Informational: the default percent applied to the reference total. The
    // charged value is the brand's own flat_rate_percent row.
    default_flat_rate_percent: number;
    rates: IReferenceRate[];
}